import {combineReducers} from "redux";
import {configureStore} from "@reduxjs/toolkit";
import authReducer from "../features/auth/model/slice";
import MessageReducer from "../features/messages/model/slice";
import usersReducer from "../features/users/model/slice";
import companiesReducer from "../features/companies/model/slice";
import filesReducer from "../features/files/model/slice";
import SuppliersReducer from "../features/suppliers/model/slice";
import InvoicesReducer from "../features/invoices/model/slice";
import shipmentsReducer from "../features/shipments/model/slice";
import ordersReducer from "../features/orders/model/slice";
import ordersPositionsReducer from "../features/orders_positions/model/slice";
import machineryReducer from "../features/machinery/model/slice";
import machineryGroupsReducer from "../features/machinery_groups/model/slice";
import machineryDocsReducer from "../features/machinery_docs/model/slice";
import machineryProblemsReducer from "../features/machinery_problems/model/slice";
import machineryTasksReducer from "../features/machinery_tasks/model/slice";
import machineryCommentsReducer from "../features/machinery_comments/model/slice";
import invoicesCommentsReducer from "../features/invoices_comments/model/slice";
import technicalLiteratureReducer from "../features/technical_literature/model/slice";
import errorCodeReducer from "../features/errorCode/model/slice";
import mapReducer from "../features/map/model/slice";
import mapHistoryReducer from "../features/map_history/model/slice";
import trackerReducer from "../features/trackers/model/slice";
import tariffReducer from "../features/tariff/models/slice";
import machineryTelemetryReducer from "../features/machinery_telemetry/models/slice";
import machineryAlertReducer from "../features/machinery_alert/model/slice";
import mlMonitorReducer from "../features/ml_monitor/model/slice";
import maintenanceReducer from "../features/maintenance/model/slice";
import emailVerificationReducer from "../features/email_verification/model/slice";
import passwordReset from "../features/password_reset/model/slice";
import billing from "../features/billing/model/slice";
import drivingEvents from "../features/driving_events/model/slice";
import clientInvoiceReducer from "../features/client_invoice/model/slice";
import issuerSettingsReducer from "../features/issuer_settings/model/slice";
import invitesReducer from "../features/invites/model/slice";
import drivingScoreReducer from "../features/driving_events/model/scoreSlice";
import backupReducer from "../features/backup/model/slice";
import fleetDashboardReducer from "../features/fleet_dashboard/model/slice";
import fuelPriceReducer from "../features/fuel_price/model/slice";
import fuelCalibrationReducer from "../features/fuel_calibration/model/slice";
import dispatchingReducer from "../features/dispatch/model/slice";
import driverDayReducer from "../features/dispatch/model/driverSlice";

const rootReducer = combineReducers({
    invoices: InvoicesReducer,
    invoicesComments: invoicesCommentsReducer,
    suppliers: SuppliersReducer,
    message: MessageReducer,
    auth: authReducer,
    shipments: shipmentsReducer,
    orders: ordersReducer,
    ordersPositions: ordersPositionsReducer,
    machinery: machineryReducer,
    machineryGroups: machineryGroupsReducer,
    machineryDocs: machineryDocsReducer,
    machineryProblems: machineryProblemsReducer,
    machineryTasks: machineryTasksReducer,
    machineryComments: machineryCommentsReducer,
    users: usersReducer,
    files: filesReducer,
    companies: companiesReducer,
    technicalLiterature: technicalLiteratureReducer,
    errorCode: errorCodeReducer,
    map: mapReducer,
    mapHistory: mapHistoryReducer,
    tracker: trackerReducer,
    tariff: tariffReducer,
    machineryTelemetry: machineryTelemetryReducer,
    alerts: machineryAlertReducer,
    mlMonitor: mlMonitorReducer,
    maintenance: maintenanceReducer,
    emailVerification: emailVerificationReducer,
    passwordReset: passwordReset,
    billing: billing,
    drivingEvents: drivingEvents,
    client_invoice: clientInvoiceReducer,
    issuer_settings: issuerSettingsReducer,
    invites: invitesReducer,
    drivingScore: drivingScoreReducer,
    backup: backupReducer,
    fleetDashboard: fleetDashboardReducer,
    fuelPrice: fuelPriceReducer,
    fuelCalibration: fuelCalibrationReducer,
    // Диспетчеризация. Ключ "dispatching" (не "dispatch") — чтобы не путать
    // state.dispatch с функцией dispatch в коде и devtools.
    dispatching: dispatchingReducer,
    // Кабинет водителя — отдельный срез: свой рейс/история, не мешаем доске.
    driverDay: driverDayReducer,
});

const isProduction = process.env.NODE_ENV === 'production';

export const setupStore = () => {
    return configureStore({
        reducer: rootReducer,

        devTools: isProduction
            ? false
            : {
                maxAge: 30,

                stateSanitizer: (state: any) => ({
                    ...state,

                    // ключ из combineReducers: "mapHistory"
                    // тяжёлое поле: list (ITrackPoint[])
                    mapHistory: state.mapHistory ? {
                        ...state.mapHistory,
                        list: `[${state.mapHistory.list?.length ?? 0} track points — hidden]`,
                    } : state.mapHistory,

                    // ключ из combineReducers: "machineryTelemetry"
                    // тяжёлые поля: все массивы истории из fetchTelemetryDashboard
                    machineryTelemetry: state.machineryTelemetry ? {
                        ...state.machineryTelemetry,
                        fuelHistory: `[${state.machineryTelemetry.fuelHistory?.length ?? 0} points — hidden]`,
                        voltageHistory: `[${state.machineryTelemetry.voltageHistory?.length ?? 0} points — hidden]`,
                        engineHistory: `[${state.machineryTelemetry.engineHistory?.length ?? 0} points — hidden]`,
                        fuelEvents: `[${state.machineryTelemetry.fuelEvents?.length ?? 0} items — hidden]`,
                        dailyMileage: `[${state.machineryTelemetry.dailyMileage?.length ?? 0} items — hidden]`,
                        dailyFuelConsumption: `[${state.machineryTelemetry.dailyFuelConsumption?.length ?? 0} items — hidden]`,
                        dailyMovement: `[${state.machineryTelemetry.dailyMovement?.length ?? 0} items — hidden]`,
                    } : state.machineryTelemetry,
                }),

                actionSanitizer: (action: any) => {
                    // WebSocket — slice name: "map_history_slice", reducer: setHistoryTrackPoints
                    if (action.type === 'map_history_slice/setHistoryTrackPoints') {
                        return {
                            ...action,
                            payload: `[${action.payload?.length ?? 0} track points — hidden]`,
                        };
                    }

                    // createAsyncThunk первый аргумент: "machinery_telemetry/dashboard"
                    if (action.type === 'machinery_telemetry/dashboard/fulfilled') {
                        return {
                            ...action,
                            payload: {
                                ...action.payload,
                                fuelHistory: `[${action.payload?.fuelHistory?.length ?? 0} points — hidden]`,
                                voltageHistory: `[${action.payload?.voltageHistory?.length ?? 0} points — hidden]`,
                                engineHistory: `[${action.payload?.engineHistory?.length ?? 0} points — hidden]`,
                                fuelEvents: `[${action.payload?.fuelEvents?.length ?? 0} items — hidden]`,
                                dailyMileage: `[${action.payload?.dailyMileage?.length ?? 0} items — hidden]`,
                                dailyFuelConsumption: `[${action.payload?.dailyFuelConsumption?.length ?? 0} items — hidden]`,
                                dailyMovement: `[${action.payload?.dailyMovement?.length ?? 0} items — hidden]`,
                            },
                        };
                    }
                    // fetchLatestTelemetry — payload это одиночный объект, не массив, не скрываем
                    return action;
                },
            },
    });
};

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore["dispatch"];
